import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { getSettings, updateSettings } from '../utils/secureFunctions';
import TopNavigation from '../components/TopNavigation';
import { useNavigate } from 'react-router-dom';

interface UserSettings {
  whoCanSeeMyPosts: 'everyone' | 'followers' | 'nobody';
  whoCanMessage: 'everyone' | 'followers' | 'nobody';
  notifyOnLikes: boolean;
  notifyOnComments: boolean;
  notifyOnFollows: boolean;
  notifyOnMentions: boolean;
  blockedUsers: string[];
  defaultAudience?: 'public' | 'friends';
}

interface UserData {
  followers: number;
  verified: boolean;
  verificationApplicationDate?: any;
  verificationStatus?: 'pending' | 'approved' | 'rejected';
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    try { return window.innerWidth <= 768; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => { try { setIsMobile(window.innerWidth <= 768); } catch {} };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [settings, setSettings] = useState<UserSettings>({
    whoCanSeeMyPosts: 'everyone',
    whoCanMessage: 'everyone',
    notifyOnLikes: true,
    notifyOnComments: true,
    notifyOnFollows: true,
    notifyOnMentions: true,
    blockedUsers: [],
    defaultAudience: 'public',
  });
  const [userData, setUserData] = useState<UserData>({
    followers: 0,
    verified: false,
    verificationStatus: undefined,
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'privacy' | 'notifications' | 'blocked' | 'verification' | 'artist'>('privacy');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    if (!auth.currentUser) return;
    try {
      const res = await getSettings();
      setSettings({
        whoCanSeeMyPosts: res.settings.whoCanSeeMyPosts,
        whoCanMessage: res.settings.whoCanMessage,
        notifyOnLikes: res.settings.notifyOnLikes,
        notifyOnComments: res.settings.notifyOnComments,
        notifyOnFollows: res.settings.notifyOnFollows,
        notifyOnMentions: res.settings.notifyOnMentions,
        blockedUsers: res.settings.blockedUsers || [],
        defaultAudience: res.settings.defaultAudience || 'public',
      });
      setUserData({
        followers: res.userData.followers || 0,
        verified: !!res.userData.verified,
        verificationApplicationDate: res.userData.verificationApplicationDate,
        verificationStatus: res.userData.verificationStatus as any,
      });
    } catch (error) {
      console.error('Failed to load settings', error);
    }
  };

  const handleSave = async () => {
    if (!auth.currentUser) return;
    
    setSaving(true);
    try {
      await updateSettings({
        whoCanSeeMyPosts: settings.whoCanSeeMyPosts,
        whoCanMessage: settings.whoCanMessage,
        notifyOnLikes: settings.notifyOnLikes,
        notifyOnComments: settings.notifyOnComments,
        notifyOnFollows: settings.notifyOnFollows,
        notifyOnMentions: settings.notifyOnMentions,
        defaultAudience: settings.defaultAudience || 'public',
        blockedUsers: settings.blockedUsers || [],
      });
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.container}>
  <TopNavigation />
      <div style={styles.main}>
        <div style={styles.settingsCard}>
          <h2 style={styles.title}>Settings</h2>

          {/* Tabs */}
          <div
            style={
              isMobile
                ? { ...styles.tabs, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }
                : { ...styles.tabs, flexWrap: 'wrap' as const, rowGap: 8 }
            }
          >
            <button
              style={{ ...(activeTab === 'privacy' ? styles.tabActive : styles.tab), ...(isMobile ? styles.tabMobile : {}) }}
              onClick={() => setActiveTab('privacy')}
            >
              🔒 Privacy
            </button>
            <button
              style={{ ...(activeTab === 'notifications' ? styles.tabActive : styles.tab), ...(isMobile ? styles.tabMobile : {}) }}
              onClick={() => setActiveTab('notifications')}
            >
              🔔 Notifications
            </button>
            <button
              style={{ ...(activeTab === 'blocked' ? styles.tabActive : styles.tab), ...(isMobile ? styles.tabMobile : {}) }}
              onClick={() => setActiveTab('blocked')}
            >
              🚫 Blocked Users
            </button>
            <button
              style={{ ...(activeTab === 'verification' ? styles.tabActive : styles.tab), ...(isMobile ? styles.tabMobile : {}) }}
              onClick={() => setActiveTab('verification')}
            >
              ✓ Verification
            </button>
            <button
              style={{ ...(activeTab === 'artist' ? styles.tabActive : styles.tab), ...(isMobile ? styles.tabMobile : {}) }}
              onClick={() => setActiveTab('artist')}
            >
              🎵 Artist Studio
            </button>
          </div>

          <div style={styles.content}>
            {/* Privacy Tab */}
            {activeTab === 'privacy' && (
              <div>
                <h3 style={styles.sectionTitle}>Privacy Settings</h3>
                
                <div style={styles.settingItem}>
                  <label style={styles.label}>Default audience for new posts</label>
                  <select
                    value={settings.defaultAudience || 'public'}
                    onChange={(e) => setSettings({ ...settings, defaultAudience: e.target.value as any })}
                    style={styles.select}
                  >
                    <option value="public">Public</option>
                    <option value="friends">Friends</option>
                  </select>
                </div>

                <div style={styles.settingItem}>
                  <label style={styles.label}>Who can see my posts?</label>
                  <select
                    value={settings.whoCanSeeMyPosts}
                    onChange={(e) => setSettings({ ...settings, whoCanSeeMyPosts: e.target.value as any })}
                    style={styles.select}
                  >
                    <option value="everyone">Everyone</option>
                    <option value="followers">Followers Only</option>
                    <option value="nobody">Nobody</option>
                  </select>
                </div>

                <div style={styles.settingItem}>
                  <label style={styles.label}>Who can message me?</label>
                  <select
                    value={settings.whoCanMessage}
                    onChange={(e) => setSettings({ ...settings, whoCanMessage: e.target.value as any })}
                    style={styles.select}
                  >
                    <option value="everyone">Everyone</option>
                    <option value="followers">Followers Only</option>
                    <option value="nobody">Nobody</option>
                  </select>
                </div>
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div>
                <h3 style={styles.sectionTitle}>Notification Preferences</h3>
                
                <div style={styles.settingItem}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={settings.notifyOnLikes}
                      onChange={(e) => setSettings({ ...settings, notifyOnLikes: e.target.checked })}
                      style={styles.checkbox}
                    />
                    <span>Notify me when someone likes my content</span>
                  </label>
                </div>

                <div style={styles.settingItem}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={settings.notifyOnComments}
                      onChange={(e) => setSettings({ ...settings, notifyOnComments: e.target.checked })}
                      style={styles.checkbox}
                    />
                    <span>Notify me when someone comments</span>
                  </label>
                </div>

                <div style={styles.settingItem}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={settings.notifyOnFollows}
                      onChange={(e) => setSettings({ ...settings, notifyOnFollows: e.target.checked })}
                      style={styles.checkbox}
                    />
                    <span>Notify me when someone follows me</span>
                  </label>
                </div>

                <div style={styles.settingItem}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={settings.notifyOnMentions}
                      onChange={(e) => setSettings({ ...settings, notifyOnMentions: e.target.checked })}
                      style={styles.checkbox}
                    />
                    <span>Notify me when someone mentions me</span>
                  </label>
                </div>
              </div>
            )}

            {/* Blocked Users Tab */}
            {activeTab === 'blocked' && (
              <div>
                <h3 style={styles.sectionTitle}>Blocked Users</h3>
                {settings.blockedUsers.length === 0 ? (
                  <p style={styles.emptyState}>You haven't blocked anyone yet.</p>
                ) : (
                  <div>
                    {settings.blockedUsers.map((userId) => (
                      <div key={userId} style={styles.blockedUserItem}>
                        <span>User ID: {userId}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Verification Tab */}
            {activeTab === 'verification' && (
              <div>
                <h3 style={styles.sectionTitle}>Verification</h3>
                {userData.verified ? (
                  <div style={styles.verifiedBanner}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
                    <h4 style={{ margin: 0, marginBottom: 8, color: '#0B6B6F' }}>You are verified!</h4>
                    <p style={{ margin: 0, color: '#5B6E6E' }}>Your account has the blue checkmark badge.</p>
                  </div>
                ) : (
                  <div style={styles.infoBox}>
                    <p style={{ margin: 0, color: '#5B6E6E' }}>Creators, music artists and public figures can apply for verification. Our team reviews every application.</p>
                  </div>
                )}
                <button onClick={() => navigate('/artist-verification')} style={styles.primaryButton}>
                  Open verification centre
                </button>
              </div>
            )}
            {/* Artist Studio Tab */}
            {activeTab === 'artist' && (
              <div>
                <h3 style={styles.sectionTitle}>Artist Studio</h3>
                
                <div style={styles.infoBox}>
                  <h4 style={{ margin: 0, marginBottom: 12 }}>Upload Your Original Music</h4>
                  <p style={{ margin: 0, marginBottom: 16, color: '#5B6E6E' }}>
                    Share your original music with the Belivah community. Your songs will be available 
                    for users to add to their videos, and you'll earn a share of ad revenue from videos 
                    that feature your music.
                  </p>
                  
                  <h4 style={{ margin: '20px 0 12px 0' }}>How It Works:</h4>
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#5B6E6E' }}>
                    <li>Upload your original songs (must own full rights)</li>
                    <li>Your music appears in the video editor's music library</li>
                    <li>Users can add your songs to their videos</li>
                    <li>You earn revenue from ads shown on videos using your music</li>
                  </ul>

                  <h4 style={{ margin: '20px 0 12px 0' }}>Terms & Conditions:</h4>
                  <ul style={{ margin: 0, paddingLeft: 20, color: '#5B6E6E', fontSize: 14 }}>
                    <li>You must own all rights to the music you upload</li>
                    <li>By uploading, you grant Belivah a non-exclusive license to use your music</li>
                    <li>You retain full ownership of your content</li>
                    <li>Revenue sharing: 60% to artist, 40% to platform</li>
                    <li>Monthly payouts for earnings over $50</li>
                  </ul>
                </div>

                <div style={{ marginTop: 24 }}>
                  <button
                    style={styles.primaryButton}
                    onClick={() => navigate('/music-library')}
                  >
                    Upload Your Music
                  </button>
                  <button
                    style={{ ...styles.secondaryButton, marginLeft: 12 }}
                    onClick={() => navigate('/music-library')}
                  >
                    View My Library
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={styles.actions}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={styles.saveButton}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              onClick={() => navigate('/profile')}
              style={styles.cancelButton}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
            >
              Back to Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5EDE1',
  },
  main: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  settingsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '30px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#072B2C',
    marginBottom: '20px',
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
    borderBottom: '2px solid #E0E0E0',
  },
  tab: {
    padding: '10px 16px',
    backgroundColor: '#F7F7F7',
    border: '1px solid #E0E0E0',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#072B2C',
    fontWeight: '500',
    transition: 'all 0.2s',
    borderRadius: 8,
    marginBottom: -2,
  },
  tabActive: {
    padding: '10px 16px',
    backgroundColor: '#E6F5F5',
    border: '1px solid #0B6B6F',
    borderBottom: '3px solid #17A2A9',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#17A2A9',
    fontWeight: '600',
    borderRadius: 8,
    marginBottom: -2,
  },
  tabMobile: {
    fontSize: '12px',
    padding: '8px 10px',
    textAlign: 'center' as const,
  },
  content: {
    minHeight: '300px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#072B2C',
    marginBottom: '20px',
  },
  settingItem: {
    marginBottom: '25px',
  },
  label: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '500',
    color: '#072B2C',
    marginBottom: '8px',
  },
  select: {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    borderRadius: '8px',
    border: '2px solid #17A2A9',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '16px',
    color: '#072B2C',
    cursor: 'pointer',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    color: '#5B6E6E',
    fontSize: '16px',
    padding: '40px 20px',
  },
  blockedUserItem: {
    padding: '12px',
    backgroundColor: '#F5F5F5',
    borderRadius: '8px',
    marginBottom: '10px',
    fontSize: '14px',
    color: '#072B2C',
  },
  verifiedBanner: {
    padding: '30px',
    backgroundColor: '#E8F5F5',
    borderRadius: '12px',
    textAlign: 'center',
    border: '2px solid #0B6B6F',
  },
  pendingBanner: {
    padding: '30px',
    backgroundColor: '#FFF8E1',
    borderRadius: '12px',
    textAlign: 'center',
    border: '2px solid #B36B00',
  },
  rejectedBanner: {
    padding: '30px',
    backgroundColor: '#FFE6E6',
    borderRadius: '12px',
    textAlign: 'center',
    border: '2px solid #B71C1C',
  },
  infoBox: {
    padding: '20px',
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    marginBottom: '20px',
  },
  primaryButton: {
    padding: '12px 30px',
    backgroundColor: '#0B6B6F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s',
  },
  secondaryButton: {
    padding: '12px 30px',
    backgroundColor: '#FFFFFF',
    color: '#0B6B6F',
    border: '2px solid #0B6B6F',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s',
  },
  actions: {
    display: 'flex',
    gap: '15px',
    marginTop: '30px',
    justifyContent: 'center',
  },
  saveButton: {
    padding: '12px 30px',
    backgroundColor: '#0B6B6F',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s',
  },
  cancelButton: {
    padding: '12px 30px',
    backgroundColor: '#E0E0E0',
    color: '#072B2C',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'transform 0.2s',
  },
};

export default Settings;
